import { forceCollide, forceLink, forceManyBody, forceRadial, forceSimulation, type Simulation } from 'd3-force'
import type { GraphLinkDatum, GraphNodeDatum } from './graphModel'

/** The spec's numbers, named so the tests and the component read one source. */
export const FORCE = {
  linkDistanceNear: 105,
  linkDistanceFar: 68,
  linkStrength: 0.9,
  charge: -230,
  collidePadding: 16,
  radialPerDepth: 85,
  radialStrength: 0.3,
  /** The root alone is held to the centre firmly; with one site carrying a
   *  hundred links, 0.3 let the star drag the enterprise off the hub. */
  rootRadialStrength: 1,
  velocityDecay: 0.42,
  alphaDecay: 0.022,
  entranceAlpha: 0.8,
  reheatAlpha: 0.3,
  expandAlpha: 0.6,
} as const

/** The spec's distance, plus the target's ring when it belongs to a large sibling group. */
export const linkDistance = (l: GraphLinkDatum): number =>
  (l.target.depth < 2 ? FORCE.linkDistanceNear : FORCE.linkDistanceFar) + l.target.ringOffset

/** Collide radius follows the animated scale, so shrunken context nodes let neighbours drift closer. */
export const collideRadius = (d: GraphNodeDatum): number => d.r * d.s + FORCE.collidePadding

/**
 * A second, softer collision for the LABEL under each node, so file names do
 * not print over each other. Half the label's width, because a label is wide
 * and short and is drawn centred; the node collision above stays the spec's.
 *
 * It was 75% of that half-width — a compromise for the demo graph, where
 * labels are short and nothing is crowded. On a real tenant (~180 sheets and
 * equipment, 26-character ids) the remaining quarter was exactly the amount
 * two neighbours' labels printed over each other by, ring after ring. The
 * full half-width is what "the labels do not overlap" actually requires.
 */
export const LABEL_CLEARANCE = 1.0
export const labelCollideRadius = (d: GraphNodeDatum): number => Math.max(0, (d.labelWidth / 2) * LABEL_CLEARANCE * d.s)
export const LABEL_COLLIDE_STRENGTH = 0.5

/**
 * Cancels the system's net angular momentum every tick. d3's collision
 * forces resolve overlaps in quadtree order, and on a crowded ring that bias
 * adds up to a slow, steady rotation of the whole graph (measured at 2–3°/s,
 * never stopping). Removing only the SHARED rotation leaves every local
 * motion intact; nothing but the spin changes.
 */
export const forceNoSpin = () => {
  let nodes: GraphNodeDatum[] = []
  const force = () => {
    if (nodes.length < 2) return
    let cx = 0, cy = 0
    for (const d of nodes) { cx += d.x; cy += d.y }
    cx /= nodes.length; cy /= nodes.length
    let l = 0, inertia = 0
    for (const d of nodes) {
      const rx = d.x - cx, ry = d.y - cy
      l += rx * (d.vy ?? 0) - ry * (d.vx ?? 0)
      inertia += rx * rx + ry * ry
    }
    if (inertia === 0) return
    const omega = l / inertia
    for (const d of nodes) {
      if (d.fx != null) continue
      const rx = d.x - cx, ry = d.y - cy
      d.vx = (d.vx ?? 0) + omega * ry
      d.vy = (d.vy ?? 0) - omega * rx
    }
  }
  force.initialize = (n: GraphNodeDatum[]) => { nodes = n }
  return force
}

export const createSimulation = (nodes: GraphNodeDatum[], links: GraphLinkDatum[]): Simulation<GraphNodeDatum, GraphLinkDatum> =>
  forceSimulation<GraphNodeDatum, GraphLinkDatum>(nodes)
    .force('link', forceLink<GraphNodeDatum, GraphLinkDatum>(links).id((d) => d.id).distance(linkDistance).strength(FORCE.linkStrength))
    .force('charge', forceManyBody<GraphNodeDatum>().strength(FORCE.charge))
    .force('collide', forceCollide<GraphNodeDatum>().radius(collideRadius))
    .force('labelCollide', forceCollide<GraphNodeDatum>().radius(labelCollideRadius).strength(LABEL_COLLIDE_STRENGTH))
    .force(
      'radial',
      forceRadial<GraphNodeDatum>((d) => d.depth * FORCE.radialPerDepth + d.ringOffset).strength((d) =>
        d.depth === 0 ? FORCE.rootRadialStrength : FORCE.radialStrength,
      ),
    )
    // No forceCenter. It held the system's CENTRE OF MASS on the page centre,
    // which on a one-site tenant is a point near the site, not the root — so
    // the enterprise node sat off to one side. The root is pinned at the
    // origin instead (seedRadialPositions sets its fx/fy), which keeps the
    // graph from travelling during the entrance just as well, and keeps the
    // root where the reader looks for it.
    .force('nospin', forceNoSpin())
    .velocityDecay(FORCE.velocityDecay)
    .alphaDecay(FORCE.alphaDecay)
    .stop()
